import axios from 'axios';
import React, { useState } from 'react';
import { Button, Dialog, Portal } from "@chakra-ui/react";

const DownloadDialog = ({ isOpen, onClose, onConfirm }) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Choose Download Format</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <p>
                Select the sequence type to download:
              </p>
            </Dialog.Body>
            <Dialog.Footer gap="3">
              <Button 
                onClick={() => {
                  onConfirm('nucl');
                  onClose();
                }}
              >
                Nucleotide Sequence
              </Button>
              <Button 
                onClick={() => {
                  onConfirm('prot');
                  onClose();
                }}
              >
                Protein Sequence
              </Button>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline" onClick={onClose}>Cancel</Button>
              </Dialog.ActionTrigger>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const downloadFastaWithMolType = async (seqIds, job, jobId, resultMolType, downloadMolType) => {
  try {
    if (seqIds.length === 0) {
      console.error("No valid result IDs found");
      return;
    }
    
    const response = await axios.post('/api/download_results_fasta/', {
      job_id: job.id,
      seq_ids: seqIds,
      result_mol_type: resultMolType,  // What type of features were found (nucl or prot)
      download_mol_type: downloadMolType  // What the user wants to download
    });
    
    // Create a FASTA file from the response data
    const sequences = response.data;
    let fastaContent = null;
    if(downloadMolType === "nucl"){
      fastaContent = sequences.map(seq => {
        const formattedSequence = seq.sequence.match(/.{1,80}/g).join('\n');
        // return `>${seq.name} [Organism=${seq.organism}]\n${formattedSequence}`;
        return `>${seq.sample_name}_${seq.name}\n${formattedSequence}`;
      }).join('\n');
    }else{
      fastaContent = sequences.map(seq => {
        const formattedSequence = seq.sequence.match(/.{1,80}/g).join('\n');
        // return `>${seq.name.replaceAll(" ", "_")} [Gene=${seq.gene}] [Organism=${seq.organism}] [Product=${seq.product}] [Location=${seq.location_start}-${seq.location_end}] [Note=${seq.note}]\n${formattedSequence}`;
        return `>${seq.sample_name}_${seq.gene_id}\n${formattedSequence}`;
      }).join('\n');
    }

    // Create and trigger download
    const element = document.createElement("a");
    const file = new Blob([fastaContent], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = `blast-results-${jobId}-${downloadMolType}.fasta`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  } catch (err) {
    console.error('Error downloading FASTA:', err);
    alert("Error downloading results.");
    throw err;
  }
};

export const downloadFasta = async (seqIds, job, jobId, showDialog) => {
  // Map blast program to the type of results it produces
  const blast_program_to_result_mol_type = {
    "blastn": "nucl",    // nucleotide query vs nucleotide db -> nucleotide results
    "blastp": "prot",    // protein query vs protein db -> protein results (CDS features)
    "blastx": "prot",    // translated nucleotide query vs protein db -> protein results (CDS features)
    "tblastn": "nucl",   // protein query vs translated nucleotide db -> nucleotide results
    "tblastx": "nucl",   // translated query vs translated db -> nucleotide results
  };
  const resultMolType = blast_program_to_result_mol_type[job.blast_program];
  
  // Only show dialog if results are protein features (where we can download nucl or prot)
  if (resultMolType === "prot" && showDialog) {
    return new Promise((resolve, reject) => {
      showDialog((downloadMolType) => {
        downloadFastaWithMolType(seqIds, job, jobId, resultMolType, downloadMolType)
          .then(resolve)
          .catch(reject);
      });
    });
  } else {
    // For nucleotide results, download directly (no choice needed)
    return downloadFastaWithMolType(seqIds, job, jobId, resultMolType, resultMolType);
  }
};

export const downloadSelected = async (selectedSamples, results, job, jobId, showDialog) => {
  try {
    const seqIds = Array.from(selectedSamples)
      .map(index => results[index]?.seq_id)
      .filter(id => id);
    await downloadFasta(seqIds, job, jobId, showDialog);
  } catch (err) {
    console.error('Error downloading selected results:', err);
  }
};

export const downloadAll = async (filteredResults, job, jobId, showDialog) => {
  try {
    const resultIds = filteredResults
      .map(result => result.seq_id)
      .filter(id => id);
    await downloadFasta(resultIds, job, jobId, showDialog);
  } catch (err) {
    console.error('Error downloading all results:', err);
  }
};

// Custom hook to manage download dialog
export const useDownloadDialog = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);

  const showDialog = (callback) => {
    setPendingDownload(() => callback);
    setIsDialogOpen(true);
  };

  const handleDialogConfirm = (molType) => {
    if (pendingDownload) {
      pendingDownload(molType);
      setPendingDownload(null);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setPendingDownload(null);
  };

  return {
    isDialogOpen,
    showDialog,
    handleDialogConfirm,
    handleDialogClose
  };
};

export { DownloadDialog };